import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, model, OnInit, Signal, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";

import { debounceTime, finalize, tap } from "rxjs";

import { LoggerService } from "@core/services/logger.service";
import { DocumentListItemModel } from "@documentation/models/document.model";
import { FolderModel } from "@documentation/models/folder.model";
import { DocumentHttpService } from "@documentation/services/document-http.service";
import { FolderHttpService } from "@documentation/services/folder-http.service";
import { ButtonWithLoadingComponent } from "@shared/components/button-with-loading/button-with-loading.component";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { InputComponent } from "@shared/components/input/input.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { ModalService } from "@shared/components/modal/modal.service";
import { ToastService } from "@shared/services/toast.service";

enum Mode {
  Move,
  CreateFolder
}

@Component({
  selector: "component-move-folder-or-document-modal",
  imports: [
    InputComponent,
    ModalComponent,
    FormsModule,
    CustomIconComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    ButtonWithLoadingComponent
  ],
  templateUrl: "./move-folder-or-document-modal.component.html",
  styleUrl: "./move-folder-or-document-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MoveFolderOrDocumentModalComponent extends BaseModalComponent implements OnInit {
  private static readonly SEARCH_DEBOUNCE_TIME_MS = 300;

  readonly document = model.required<DocumentListItemModel>();

  protected readonly mode = signal<Mode>(Mode.Move);
  protected readonly selectedFolder = signal<FolderModel | null>(null);
  protected readonly searchValue = signal<string>("");
  protected readonly isProcessing = signal<boolean>(false);
  protected readonly isFetchingList = signal<boolean>(false);
  private readonly _debouncedSearchValue = signal<string>("");

  protected readonly foldersToDisplay: Signal<FolderModel[]>;
  protected readonly isMoveDisabled: Signal<boolean>;
  protected readonly isCreateFolderMode: Signal<boolean>;
  private readonly _folders = signal<FolderModel[]>([]);

  protected readonly control: FormControl<string> = new FormControl("", { validators: [Validators.required], nonNullable: true });

  protected readonly Mode = Mode;

  protected _hasUpdates: boolean = false;

  private readonly _documentHttpService = inject(DocumentHttpService);
  private readonly _folderHttpService = inject(FolderHttpService);
  private readonly _logger = inject(LoggerService);
  private readonly _toast = inject(ToastService);
  private readonly _modalService = inject(ModalService);

  constructor() {
    super();

    this.foldersToDisplay = computed(() => this.foldersToDisplayComputation());
    this.isMoveDisabled = computed(() => this.isMoveDisabledComputation());
    this.isCreateFolderMode = computed(() => this.mode() === Mode.CreateFolder);

    this.debounceSearchValue();

    // Reset selected folder on search
    toObservable(this._debouncedSearchValue).subscribe(() => this.selectedFolder.set(null));
  }

  ngOnInit(): void {
    this.loadFolders();
  }

  protected async onMove(): Promise<void> {
    if (this.isMoveDisabled()) return;

    // Confirm moving private document into a public folder
    if (
      this.selectedFolder()?.isPublic &&
      !this.document().isPublic &&
      !(await this._modalService.confirmMoveDocIntoPublicFolder())
    )
      return;

    this.isProcessing.set(true);

    this._documentHttpService
      .moveDocumentToFolder(this.document().id, this.selectedFolder()!?.id)
      .pipe(finalize(() => this.isProcessing.set(false)))
      .subscribe({
        next: () => {
          this._hasUpdates = true;
          this.close();
          this._toast.success(`${this.document().title} successfully moved to ${this.selectedFolder()!.name}`);
        },
        error: (e) => {
          this._toast.error("Failed to move document");
          this._logger.error("Failed to move the document", e);
        }
      });
  }

  protected onCreateFolder(): void {
    if (this.control.invalid) return;

    this.isProcessing.set(true);

    this._folderHttpService.createFolder(this.control.value).subscribe({
      next: (folder) => {
        this._hasUpdates = true;
        this.selectedFolder.set(folder);
        this.searchValue.set("");
        this.onMove();
      },
      error: (e) => {
        this._toast.error("Failed to create folder");
        this._logger.error("Failed to create folder", e);
        this.isProcessing.set(false);
      }
    });
  }

  protected setMode(mode: Mode): void {
    switch (mode) {
      case Mode.CreateFolder:
        this.control.reset();
        this.mode.set(mode);
        break;
      default:
        this.mode.set(mode);
    }
  }

  protected close(): void {
    this.activeModal.close(this._hasUpdates);
  }

  private debounceSearchValue(): void {
    toObservable(this.searchValue)
      .pipe(
        tap((val) => (val === "" ? this._debouncedSearchValue.set("") : null)),
        debounceTime(MoveFolderOrDocumentModalComponent.SEARCH_DEBOUNCE_TIME_MS)
      )
      .subscribe((searchValue) => this._debouncedSearchValue.set(searchValue));
  }

  private loadFolders(): void {
    this.isFetchingList.set(true);

    this._folderHttpService
      .getAllUserFolders()
      .pipe(finalize(() => this.isFetchingList.set(false)))
      .subscribe({
        next: (folders) => this._folders.set(folders),
        error: (e) => {}
      });
  }

  private foldersToDisplayComputation(): FolderModel[] {
    const query = this._debouncedSearchValue().trim().toLowerCase();

    if (!query.length) return this._folders();

    return this._folders().filter(({ name }) => name.toLowerCase().includes(query));
  }

  private isMoveDisabledComputation(): boolean {
    return !this.selectedFolder() || this.selectedFolder()?.id === this.document().folderId;
  }
}
